import { API_URL } from './api';

/**
 * Bulk loader + cache for player ELO/rank keyed by steamid64.
 *
 * Mirrors ``utils/countries.js``: the Online-sidebar warms the cache for
 * everyone currently online with a single ``/api/elo/bulk`` request and
 * re-renders via {@link onEloLoaded} once a batch resolves. Only steamids
 * with a PlayerElo record are returned by the API; unranked/unknown ids stay
 * absent from the cache (no rank rendered).
 */
export const eloCache = {};
let eloFetched = new Set();
let eloListeners = [];

/**
 * Subscribe to cache updates (a resolved bulk fetch).
 *
 * @param {Function} callback - Called with no args when new ELO data landed.
 * @returns {Function} Unsubscribe function.
 */
export const onEloLoaded = (callback) => {
  eloListeners.push(callback);
  return () => {
    eloListeners = eloListeners.filter((cb) => cb !== callback);
  };
};

const notifyEloListeners = () => {
  eloListeners.forEach((cb) => cb());
};

/**
 * Fetch ELO/rank for a batch of steamids, skipping ones already cached or
 * in flight. Safe to call on every presence update — only new ids trigger
 * a network request.
 *
 * @param {string[]} steamids - steamid64 strings (falsy entries ignored).
 * @returns {Promise<object>} The shared cache ({steamid64: {elo, rank, ...}}).
 */
export const fetchEloBulk = async (steamids) => {
  const newIds = steamids.filter(
    (id) => id && !eloCache[id] && !eloFetched.has(String(id)),
  );
  if (newIds.length === 0) return eloCache;

  newIds.forEach((id) => eloFetched.add(String(id)));

  try {
    const response = await fetch(`${API_URL}/api/elo/bulk?steamids=${newIds.join(',')}`);
    const data = await response.json();
    if (data.results) {
      Object.assign(eloCache, data.results);
      notifyEloListeners();
    }
  } catch (e) {
    console.error('Failed to fetch ELO bulk:', e);
  }

  return eloCache;
};
